import classNames from 'classnames';
import { forwardRef, PropsWithChildren } from 'react';

import { Skeleton, SkeletonProps } from '../../skeleton';

export interface CardTitleProps extends React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
  className?: string;
  variant?: 'default' | 'dense' | 'none' | 'highlighted' | 'large';
  align?: 'left' | 'center' | 'right';
  emptyValue?: React.ReactNode;
  isEmpty?: boolean;
  loading?: boolean;
  SkeletonProps?: SkeletonProps;
}

/**
 * Title component for cards
 */
const CardTitle = forwardRef<HTMLDivElement, PropsWithChildren<CardTitleProps>>((props, ref) => {
  const {
    children,
    className,
    variant = 'default',
    align = 'left',
    emptyValue = '-',
    isEmpty = false,
    loading = false,
    SkeletonProps,
    ...otherProps
  } = props;

  return (
    <div
      {...otherProps}
      ref={ref}
      className={classNames(
        'flex-1',
        className,
        variant === 'default' && 'font-regular leading-tight text-xl text-white dark:text-white',
        variant === 'dense' && 'font-medium leading-tight uppercase text-xs text-white dark:text-white',
        variant === 'large' && 'font-medium leading-tight text-2xl text-white dark:text-white',
        variant === 'highlighted' && 'font-medium leading-tight uppercase text-xs text-brand-primary',
        align === 'left' && 'text-left',
        align === 'center' && 'text-center',
        align === 'right' && 'text-right',
      )}
    >
      {loading && (
        <Skeleton
          variant="text"
          {...SkeletonProps}
          className={classNames(
            SkeletonProps?.className ? SkeletonProps?.className : 'w-[35%] max-w[128px]',
            SkeletonProps?.className && align === 'right' && 'ml-auto',
          )}
        />
      )}
      {!loading && (isEmpty ? emptyValue : children)}
    </div>
  );
});

CardTitle.displayName = 'CardTitle';

export { CardTitle };

export default CardTitle;
